/**
 * BlockFormDialog Component
 *
 * Modal dialog for creating and editing strategy blocks.
 * Simplified version of StrategyFormDialog adapted for blocks.
 *
 * Features:
 * - Create/Edit modes
 * - Unsaved changes detection
 * - Real-time field validation
 * - AI parameters (optional for create mode)
 * - Focus management
 *
 * Related: Phase 3, BlockFormDialog implementation
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Typography, Box, Stack, CircularProgress, Button, FormControlLabel, Checkbox, Alert, IconButton, Tooltip } from '@mui/material';
import { Autorenew as AutorenewIcon, Add as AddIcon, DeleteOutline as DeleteOutlineIcon } from '@mui/icons-material';
import {
  SDialog,
  SAccordion,
  StyledTextField,
  StyledTextArea,
  StyledSelectDropdown,
  SortableList,
  UnsavedChangesDialog,
  useChangeDetection,
} from '@modules/ui-components';
import type { DialogActionConfig } from '@modules/ui-components';
import { StrategyBlockService } from '@modules/firebase-core/services/StrategyBlockService';
import { StrategyService } from '@modules/firebase-core/services/StrategyService';
import type { StrategyBlock, StrategyBlockInstruction } from '@segmently/schema-models/strategy';
import { DataMappingViewerV2 } from './DataMappingViewerV2';
import { blockContractService } from '../services/BlockContractService';
import type { SimplifiedFunnelVariable } from '@segmently/schema-models/simplified';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { StrategyExportService } from '../services/StrategyExportService';
import { useBlockSubtypes } from '../hooks/useBlockSubtypes';
import { requestBlockScreens } from '../services/requestBlockScreens';
import { STRATEGY_CANVAS } from '@test-kit/selectors/strategy-canvas';

export interface BlockFormDialogProps {
  /** Whether dialog is open */
  open: boolean;

  /** Dialog mode: 'create' or 'edit' */
  mode: 'create' | 'edit';

  /** Strategy ID for the block */
  strategyId: string;

  /** Project ID */
  projectId: string;

  /** Block ID (required for edit mode) */
  blockId?: string;

  /** Initial block data (for edit mode) */
  initialData?: StrategyBlock;

  /** Callback when dialog is closed */
  onClose: () => void;

  /** Callback when block is saved successfully */
  onSave: (block: StrategyBlock) => void;

  /** Optional ref to return focus to trigger button */
  triggerRef?: React.RefObject<HTMLElement>;

  /**
   * Optional callback for canvas integration (Command Pattern)
   * When provided, dialog will NOT save to Firebase directly.
   * Canvas controls save via EditBlockCommand for undo/redo support.
   */
  onSubmit?: (blockData: StrategyBlock) => Promise<{ success: boolean; error?: Error }>;

  /** Called after an AI block/screen generation backend task is created. */
  onGenerationTaskStarted?: (taskId: string, blockId: string) => void;

  /**
   * HB-6 (D-L2-5): «Update Block with AI» no longer regenerates from inside this
   * form. Path B — the live `CompositeDataPicker` plus
   * `StrategyOrchestrator.regenerateBlock` — is deleted, so the action CLOSES
   * this form and opens the one block-update dialog (FP-4: one modal at a time).
   * Omitted = the surface has no block-update dialog to open and the action is
   * not offered at all.
   */
  onOpenScreenGeneration?: (blockId: string) => void;
}

interface FormData {
  name: string;
  blockType: StrategyBlock['blockType'];
  description: string;
  customSubtype: string;
  instructions: StrategyBlockInstruction[];
  useAI: boolean; // Generate screens with AI (create mode only)
  aiStyle?: string; // AI style parameter
  aiContent?: string; // AI content prompt
  // FR-033: Order field removed (managed automatically)
}

interface FormErrors {
  name?: string;
  blockType?: string;
  instructions?: string;
  // FR-033: order validation removed
}

const BLOCK_TYPES: Array<NonNullable<StrategyBlock['blockType']>> = [
  'welcome',
  'user_goals',
  'user_data',
  'plan_paywall',
  'history',
  'motivation',
  'environment',
  'habits',
  'strategy',
  'custom',
];

const INSTRUCTION_LEVELS = ['mustHave', 'should', 'niceToHave', 'forbidden'] as const;

export const BlockFormDialog: React.FC<BlockFormDialogProps> = ({
  open,
  onSubmit,
  mode,
  strategyId,
  projectId,
  blockId,
  initialData,
  onClose,
  onSave,
  triggerRef,
  onGenerationTaskStarted,
  onOpenScreenGeneration,
}) => {
  // Form state
  const [formData, setFormData] = useState<FormData>({
    name: '',
    blockType: undefined as unknown as StrategyBlock['blockType'],
    description: '',
    customSubtype: '',
    instructions: [],
    useAI: false,
    aiStyle: '',
    aiContent: '',
    // FR-033: order removed from state
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [errorAlert, setErrorAlert] = useState<string | null>(null);
  /**
   * The block a create-with-AI submit already saved. Only the GENERATION request
   * can fail after it (final review Important #3), and the dialog then stays open
   * with the reason — so the next submit must reuse this block instead of
   * creating a second one. Cleared when the dialog opens.
   */
  const createdBlockRef = useRef<StrategyBlock | null>(null);
  const blockSubtypeCatalog = useBlockSubtypes(projectId);
  const assignedSubtype = useMemo(
    () => blockSubtypeCatalog.subtypes.find((item) => item.slug === formData.customSubtype) ?? null,
    [blockSubtypeCatalog.subtypes, formData.customSubtype],
  );
  const subtypeOptions = useMemo(() => {
    const visible = blockSubtypeCatalog.subtypes.filter((item) => (
      item.enabled || item.slug === formData.customSubtype
    ));
    const options = visible.map((item) => ({
      value: item.slug,
      label: `${item.name} · ${item.slug}${item.enabled ? '' : ' (disabled)'}`,
    }));
    if (formData.customSubtype && !visible.some((item) => item.slug === formData.customSubtype)) {
      options.push({ value: formData.customSubtype, label: `Unknown subtype · ${formData.customSubtype}` });
    }
    return [{ value: '', label: 'No subtype' }, ...options];
  }, [blockSubtypeCatalog.subtypes, formData.customSubtype]);

  // Service instances
  const blockService = useMemo(
    () => new StrategyBlockService(projectId, strategyId),
    [projectId, strategyId]
  );

  const strategyService = useMemo(() => new StrategyService(projectId), [projectId]);

  /**
   * HB-6 (D-L2-5): the live `CompositeDataPicker` is GONE from this form. On a
   * wizard-created strategy the server discarded everything it sent
   * (`ignoredRequestReason`), so it was a control whose value was thrown away
   * (designer invariant 1). Source data for a generation now comes from the
   * strategy — the pinned analysis, its frozen narrowing and this block's
   * citations — and is edited in the one block-update dialog, which no longer
   * writes `sourceData` / `sourceDataMapping` back onto the block here.
   */

  // Change detection for unsaved changes
  const {
    startTracking,
    updateCurrent,
    isDirty,
    getChanges,
    reset,
    acceptChanges,
    revert,
  } = useChangeDetection<FormData>({
    debug: false,
  });

  // Individual field validators
  const validateName = useCallback((value: string): string | null => {
    if (!value.trim()) {
      return 'Block name is required';
    }
    if (value.length > 100) {
      return 'Block name must be less than 100 characters';
    }
    return null;
  }, []);

  const validateBlockType = useCallback((value: StrategyBlock['blockType']): string | null => {
    if (!value) {
      return 'Block type is required';
    }
    return null;
  }, []);

  // FR-033: validateOrder removed

  // Clear field error if field becomes valid
  const clearFieldError = useCallback(
    (fieldName: keyof FormErrors, value: string | StrategyBlock['blockType']) => {
      let isValid = false;

      switch (fieldName) {
        case 'name':
          isValid = validateName(value as string) === null;
          break;
        case 'blockType':
          isValid = validateBlockType(value as StrategyBlock['blockType']) === null;
          break;
        // FR-033: order case removed
      }

      if (isValid) {
        setErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[fieldName];
          return newErrors;
        });
      }
    },
    [validateName, validateBlockType] // FR-033: validateOrder dependency removed
  );

  // Full form validation
  const validateForm = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    const nameError = validateName(formData.name);
    if (nameError) newErrors.name = nameError;

    const blockTypeError = validateBlockType(formData.blockType);
    if (blockTypeError) newErrors.blockType = blockTypeError;

    if (formData.instructions.some((instruction) => !instruction.text.trim())) {
      newErrors.instructions = 'Every instruction must include text.';
    }

    // FR-033: order validation removed

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, validateName, validateBlockType]); // FR-033: validateOrder dependency removed

  // ✅ Filtered source data is now provided by orchestrator
  // No need for 7 useMemo hooks - all handled in useDataReferenceOrchestrator

  // Load strategy (for variables)
  const [strategyVariables, setStrategyVariables] = useState<Record<string, any>>({});
  useEffect(() => {
    if (open && projectId) {
      strategyService.get(strategyId)
        .then(strategy => {
          console.log('[BlockFormDialog] Strategy loaded:', strategy?.id);
          setStrategyVariables((strategy as any)?.variables ?? {});
        })
        .catch(error => {
          console.error('Failed to load strategy:', error);
        });
    }
  }, [open, projectId, strategyId, strategyService]);

  // ✅ Initialize form data when dialog opens
  useEffect(() => {
    if (!open) return;
    createdBlockRef.current = null;
    setErrorAlert(null);

    if (mode === 'edit' && initialData) {
      const editData: FormData = {
        name: initialData.name || '',
        blockType: initialData.blockType,
        description: initialData.description || '',
        customSubtype: initialData.customSubtype || '',
        instructions: (initialData.instructions || []).map((instruction) => ({ ...instruction })),
        useAI: false,
        aiStyle: '',
        aiContent: '',
        // FR-033: order removed from edit data
      };
      setFormData(editData);
      startTracking(editData);
      console.log('[BlockFormDialog] Edit mode - form initialized:', {
        name: editData.name,
        blockType: editData.blockType,
      });
    } else if (mode === 'create') {
      const emptyData: FormData = {
        name: '',
        blockType: 'welcome',
        description: '',
        customSubtype: '',
        instructions: [],
        useAI: false,
        aiStyle: '',
        aiContent: '',
        // FR-033: order removed from create data
      };
      setFormData(emptyData);
      startTracking(emptyData);
    }
  }, [open, mode, initialData, startTracking]);

  // Return focus to trigger when dialog closes
  const returnFocus = useCallback(() => {
    setTimeout(() => {
      if (triggerRef?.current) {
        triggerRef.current.focus();
      }
    }, 100);
  }, [triggerRef]);

  // Handle field changes with change tracking and real-time validation
  const handleFieldChange = useCallback(
    (field: keyof FormData, value: FormData[keyof FormData]) => {
      setFormData(prev => {
        const newData = { ...prev, [field]: value };
        updateCurrent(newData);
        return newData;
      });

      if (typeof value === 'string' || typeof value === 'number') {
        clearFieldError(field as keyof FormErrors, String(value));
      }
    },
    [updateCurrent, clearFieldError]
  );

  const handleBlockTypeChange = useCallback((value: StrategyBlock['blockType']) => {
    setFormData((current) => {
      const next = {
        ...current,
        blockType: value,
        ...(value === 'custom' ? {} : { customSubtype: '' }),
      };
      updateCurrent(next);
      return next;
    });
    clearFieldError('blockType', value);
  }, [clearFieldError, updateCurrent]);

  const handleSubtypeChange = useCallback((value: string | number | (string | number)[]) => {
    // Single-select dropdown — the value is always the subtype slug string.
    const slug = String(value);
    const subtype = blockSubtypeCatalog.subtypes.find((item) => item.slug === slug);
    setFormData((current) => {
      const next = {
        ...current,
        customSubtype: slug,
        ...(subtype ? {
          instructions: subtype.defaultInstructions.map((instruction) => ({
            ...instruction,
            id: instruction.id || crypto.randomUUID(),
          })),
        } : {}),
      };
      updateCurrent(next);
      return next;
    });
  }, [blockSubtypeCatalog.subtypes, updateCurrent]);

  const addInstruction = useCallback(() => {
    handleFieldChange('instructions', [
      ...formData.instructions,
      { id: crypto.randomUUID(), level: 'should', text: '' },
    ]);
  }, [formData.instructions, handleFieldChange]);

  const updateInstruction = useCallback((index: number, patch: Partial<StrategyBlockInstruction>) => {
    handleFieldChange('instructions', formData.instructions.map((instruction, instructionIndex) => (
      instructionIndex === index ? { ...instruction, ...patch } : instruction
    )));
  }, [formData.instructions, handleFieldChange]);

  const removeInstruction = useCallback((index: number) => {
    handleFieldChange('instructions', formData.instructions.filter((_, instructionIndex) => instructionIndex !== index));
  }, [formData.instructions, handleFieldChange]);

  const blockSubtypeMetadata = useMemo(() => ({
    customSubtype: formData.blockType === 'custom' && formData.customSubtype
      ? formData.customSubtype
      : undefined,
    instructions: formData.instructions.map((instruction) => ({
      ...instruction,
      text: instruction.text.trim(),
    })),
  }), [formData.blockType, formData.customSubtype, formData.instructions]);

  // Handle save
  const handleSubmit = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      let savedBlock: StrategyBlock;

      if (mode === 'create') {
        console.log('[BlockFormDialog] Creating block...');
        console.log('[BlockFormDialog] UseAI:', formData.useAI);

        if (formData.useAI) {
          // Create block with AI
          console.log('[BlockFormDialog] 🎨 Creating block with AI...');

          // A retry after a failed generation request must NOT create a second
          // block: the first one is already saved and the generation is what
          // failed (final review Important #3).
          savedBlock = createdBlockRef.current ?? await blockService.create({
            name: formData.name,
            blockType: formData.blockType,
            description: formData.description || '',
            ...blockSubtypeMetadata,
            // FR-033: order removed (will be managed automatically)
          } as any);
          createdBlockRef.current = savedBlock;

          console.log('[BlockFormDialog] ✅ Block created in DB:', savedBlock.id);

          // Then ask the server to generate this block's screens.
          const aiPrompt = formData.aiContent || `Generate screens for ${formData.blockType} block with style: ${formData.aiStyle || 'default'}`;

          // HB-6 (D-L2-5): the SAME Path A request the block-update dialog
          // sends. Final review Important #3: the request itself lives in
          // `requestBlockScreens` — components are UI + layout — and its failure
          // is stated INLINE, never through `alert()` (F-LIVE2-44).
          const generationResult = await requestBlockScreens({
            projectId,
            strategyId,
            block: savedBlock as StrategyBlock & { id: string },
            prompt: aiPrompt,
          });

          if (!generationResult.success) {
            console.error('[BlockFormDialog] ❌ AI generation failed:', generationResult.error);
            // The block IS saved — the dialog stays open with the reason instead
            // of closing on a half-done action, and a retry reuses that block
            // (`createdBlockRef`) rather than creating a second one.
            setErrorAlert(generationResult.error || 'AI generation failed.');
            setIsSubmitting(false);
            return;
          }

          if (generationResult.taskId && savedBlock.id) {
            onGenerationTaskStarted?.(generationResult.taskId, savedBlock.id);
          }
        } else {
          // Create block without AI
          console.log('[BlockFormDialog] Creating block without AI');

          savedBlock = await blockService.create({
            name: formData.name,
            blockType: formData.blockType,
            description: formData.description || '',
            ...blockSubtypeMetadata,
            // FR-033: order removed (will be managed automatically)
          } as any);

          console.log('[BlockFormDialog] Block created:', savedBlock.id);
        }
      } else {
        // Update existing block
        if (!blockId) {
          throw new Error('Block ID is required for edit mode');
        }

        console.log('[BlockFormDialog] Updating block...');

        // Check if onSubmit callback provided (canvas mode)
        if (onSubmit) {
          // Canvas mode: use callback for Command Pattern
          console.log('[BlockFormDialog] Canvas mode: calling onSubmit callback');

          // HB-6: `sourceData` / `sourceDataMapping` are NOT written from here
          // any more. The picker that produced them is gone, so writing them
          // would overwrite the block's stored citations with an empty set.
          const updatedBlock = {
            ...initialData!,
            name: formData.name,
            blockType: formData.blockType,
            description: formData.description || '',
            ...blockSubtypeMetadata,
          } as StrategyBlock;

          const result = await onSubmit(updatedBlock);

          if (result.success) {
            // Success: close dialog
            console.log('[BlockFormDialog] onSubmit succeeded');
            acceptChanges();
            onClose();
            return;
          } else {
            // Failure: show error alert, keep dialog open
            console.error('[BlockFormDialog] onSubmit failed:', result.error);
            setErrorAlert(result.error?.message || 'Failed to save block');
            setIsSubmitting(false);
            return;
          }
        } else {
          // Standalone mode: save to Firebase directly
          savedBlock = await blockService.update(blockId, {
            name: formData.name,
            blockType: formData.blockType,
            description: formData.description || '',
            ...blockSubtypeMetadata,
            // FR-033: order removed (will be managed automatically)
          } as any);

          console.log('[BlockFormDialog] Block updated:', savedBlock.id);
        }
      }

      // Accept changes after successful save
      acceptChanges();

      // Call onSave callback with saved block
      onSave(savedBlock);

      // Close dialog
      onClose();

      // Reset state
      setErrors({});
      reset();

      // Return focus to trigger
      returnFocus();
    } catch (error) {
      console.error('[BlockFormDialog] Error saving block:', error);
      alert(`Failed to save block: ${(error as Error)?.message || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    validateForm,
    formData,
    blockSubtypeMetadata,
    mode,
    blockId,
    blockService,
    projectId,
    strategyId,
    onSave,
    onClose,
    acceptChanges,
    reset,
    returnFocus,
  ]);

  // Handle close with unsaved changes check
  const handleClose = useCallback(() => {
    if (isDirty()) {
      setShowUnsavedDialog(true);
      return;
    }

    onClose();
    setErrors({});
    reset();
    returnFocus();
  }, [isDirty, reset, onClose, returnFocus]);

  // Handlers for UnsavedChangesDialog
  const handleSaveAndClose = useCallback(async () => {
    if (validateForm()) {
      await handleSubmit();
    }
    setShowUnsavedDialog(false);
  }, [validateForm, handleSubmit]);

  const handleDiscardAndClose = useCallback(() => {
    const originalData = revert();
    if (originalData) {
      setFormData(originalData);
    }
    setErrors({});
    onClose();
    setShowUnsavedDialog(false);
    reset();
    returnFocus();
  }, [revert, reset, onClose, returnFocus]);

  const handleContinueEditing = useCallback(() => {
    setShowUnsavedDialog(false);
  }, []);

  /**
   * HB-6 (FP-4, one modal): «Update Block with AI» CLOSES this form and opens
   * the block-update dialog. It never runs a generation of its own, and it is
   * only reachable on a clean form — the generation reads the block as it is
   * STORED, not as it is typed here.
   */
  const handleUpdateWithAI = useCallback(() => {
    if (!blockId || !onOpenScreenGeneration || isDirty()) return;
    onClose();
    setErrors({});
    reset();
    onOpenScreenGeneration(blockId);
  }, [blockId, onOpenScreenGeneration, isDirty, onClose, reset]);

  // FR-041: Done button - save WITHOUT AI
  const handleDoneClick = useCallback(async () => {
    if (!validateForm()) {
      return;
    }

    if (!blockId) {
      console.error('[BlockFormDialog] Block ID required for edit mode');
      return;
    }

    setIsSubmitting(true);
    try {
      // Save block WITHOUT AI. HB-6: `sourceData` / `sourceDataMapping` are the
      // block's own citations — never rewritten from this form any more.
      const updatedBlock = await blockService.update(blockId, {
        name: formData.name,
        blockType: formData.blockType,
        description: formData.description || '',
        ...blockSubtypeMetadata,
      } as Partial<StrategyBlock>);

      console.log('[BlockFormDialog] Block saved without AI:', updatedBlock.id);

      // Accept changes after save
      acceptChanges();

      // Call onSave callback
      onSave(updatedBlock);

      // Close dialog
      onClose();
      setErrors({});
      reset();
      returnFocus();
    } catch (error) {
      console.error('[BlockFormDialog] Error saving block:', error);
      alert(`Failed to save block: ${(error as Error)?.message}`);
    } finally {
      setIsSubmitting(false);
    }
  }, [validateForm, blockId, formData, blockSubtypeMetadata, blockService, acceptChanges, onSave, onClose, reset, returnFocus]);

  /**
   * Export block JSON. HB-6: `StrategyOrchestrator.buildBlockPayload` is deleted
   * with the rest of Path B, so the export goes through the service that already
   * builds the very same `AgentUpdateBlockRequest` from the STRATEGY's source
   * data instead of from a picker's hydration.
   */
  const handleExportBlockJson = useCallback(async () => {
    if (!blockId) return;
    try {
      const envelope = await new StrategyExportService().exportBlockPayload(
        projectId,
        strategyId,
        blockId,
        formData.description || 'Regenerate this block',
      );

      const name = formData.name?.replace(/\s+/g, '_') || blockId;
      StrategyExportService.download(envelope, `block_${name}_${Date.now()}.json`);
    } catch (err) {
      console.error('[BlockFormDialog] Export failed:', err);
      alert(`Export failed: ${(err as Error).message}`);
    }
  }, [blockId, projectId, strategyId, formData]);

  // Memoized action objects
  // FR-038, FR-039: Mode-specific button labels
  const primaryAction: DialogActionConfig | undefined = useMemo(
    () => ({
      label: isSubmitting
        ? mode === 'create'
          ? 'Creating...'
          : 'Saving...'
        : mode === 'create'
          ? 'Create Block with AI'  // FR-038
          : 'Done',  // FR-041: Done button (save without AI)
      onClick: mode === 'create'
        ? handleSubmit
        : onSubmit
          ? handleSubmit  // If onSubmit provided: use Command Pattern
          : handleDoneClick,  // If no onSubmit: direct Firebase save
      disabled: isSubmitting,
      'data-testid': 'block-form-save-btn',
      'aria-label': mode === 'create' ? 'Create block with AI generation' : 'Save block changes',
    }),
    [isSubmitting, mode, onSubmit, handleSubmit, handleDoneClick]
  );

  /**
   * HB-6: the entry point is a labelled action IN the form (mock
   * `block-update-entry-point.html`), not a second footer button — the footer
   * keeps its one primary (FP-6). Offered only where a block-update dialog
   * exists to open.
   */
  const screenGenerationBlocked = isDirty()
    ? 'Press Done to save first — the generation reads the block as it is stored, not as it is typed here.'
    : undefined;

  // FR-030, FR-031: Dynamic dialog titles
  const dialogTitle = useMemo(() => {
    if (mode === 'create') {
      return 'Create Block with AI';
    }
    // Edit mode: show block name
    return `Edit ${initialData?.name || 'Block'}`;
  }, [mode, initialData?.name]);

  return (
    <>
      {/* Main Form Dialog */}
      <SDialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        title={dialogTitle}
        primaryAction={primaryAction}
        data-testid="block-form-dialog"
      >
        <Stack spacing={3}>
            {/* The form's ONE inline failure surface: a rejected onSubmit and a
                refused screen-generation request both land here — never in an
                `alert()` (F-LIVE2-44, final review Important #3). */}
            {errorAlert && (
              <Alert
                severity="error"
                onClose={() => setErrorAlert(null)}
                data-testid={STRATEGY_CANVAS.blockFormErrorAlert}
              >
                {errorAlert}
              </Alert>
            )}

            {/* FR-032: Flat layout - fields without section grouping */}
            <StyledTextField
              label="Block Name"
              value={formData.name}
              onChange={(value) => handleFieldChange('name', value)}
              error={Boolean(errors.name)}
              errorMessage={errors.name}
              required
              fullWidth
              maxLength={100}
              autoFocus={mode === 'create'}
              data-testid="block-form-name"
              // FR-034: helperText removed
            />

            <StyledSelectDropdown
              label="Block Type"
              value={formData.blockType || ''}
              onChange={(value) => handleBlockTypeChange(value as StrategyBlock['blockType'])}
              options={BLOCK_TYPES.map((type) => ({
                value: type,
                label: type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
              }))}
              error={Boolean(errors.blockType)}
              errorMessage={errors.blockType}
              required
              fullWidth
              data-testid="block-form-type"
              // FR-034: helperText removed
            />

            {formData.blockType === 'custom' && (
              <>
                <StyledSelectDropdown
                  label="Custom Subtype"
                  value={formData.customSubtype}
                  onChange={handleSubtypeChange}
                  options={subtypeOptions}
                  disabled={blockSubtypeCatalog.loading}
                  fullWidth
                  data-testid={STRATEGY_CANVAS.blockFormSubtype}
                />
                {formData.customSubtype && assignedSubtype?.enabled === false && (
                  <Alert severity="warning" data-testid={STRATEGY_CANVAS.blockFormSubtypeWarning}>
                    This subtype is disabled for new assignments. This block keeps its pinned subtype and instructions until you choose another option.
                  </Alert>
                )}
                {formData.customSubtype && !assignedSubtype && !blockSubtypeCatalog.loading && (
                  <Alert severity="warning" data-testid={STRATEGY_CANVAS.blockFormSubtypeWarning}>
                    The assigned subtype is no longer in the project catalog. You may keep the exact slug or choose a current subtype.
                  </Alert>
                )}
              </>
            )}

            {/* FR-033: Order field removed */}

            <StyledTextArea
              label="Description"
              value={formData.description}
              onChange={(value) => handleFieldChange('description', value)}
              fullWidth
              minRows={4}
              maxRows={8}
              maxLength={500}
              showCharacterCount
              data-testid="block-form-description"
              sx={{
                '& .MuiInputBase-root': {
                  maxHeight: '200px',
                  overflowY: 'auto',
                },
              }}
              // FR-034: helperText removed
              // FR-035: Auto-expand to 200px (maxRows=8), then scroll
            />

            <SAccordion
              title="Block Instructions"
              collapsible
              defaultExpanded={formData.instructions.length > 0 || formData.blockType === 'custom'}
              helpContent="Ordered requirements are preserved in tactician prompts and automatic post-retry review."
            >
              <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Exact levels: mustHave, should, niceToHave, forbidden.
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addInstruction}
                    data-testid={STRATEGY_CANVAS.blockFormInstructionAdd}
                  >
                    Add instruction
                  </Button>
                </Stack>
                {errors.instructions && <Alert severity="error">{errors.instructions}</Alert>}
                {formData.instructions.length > 0 ? (
                  <SortableList
                    items={formData.instructions}
                    onReorder={(items) => handleFieldChange('instructions', items)}
                    data-testid={STRATEGY_CANVAS.blockFormInstructionList}
                  >
                    {(instruction, index) => (
                      <SortableList.Item
                        showDragHandle
                        data-testid={STRATEGY_CANVAS.blockFormInstruction(index)}
                      >
                        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ width: '100%' }}>
                          <Box sx={{ width: 150, flexShrink: 0 }}>
                            <StyledSelectDropdown
                              label="Level"
                              value={instruction.level}
                              onChange={(value) => updateInstruction(index, { level: value as StrategyBlockInstruction['level'] })}
                              options={INSTRUCTION_LEVELS.map((level) => ({ value: level, label: level }))}
                              fullWidth
                            />
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <StyledTextField
                              label="Instruction"
                              value={instruction.text}
                              onChange={(value) => updateInstruction(index, { text: value })}
                              fullWidth
                            />
                          </Box>
                          <Tooltip title="Remove instruction">
                            <IconButton aria-label="Remove instruction" onClick={() => removeInstruction(index)}>
                              <DeleteOutlineIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </SortableList.Item>
                    )}
                  </SortableList>
                ) : (
                  <Alert severity="info" variant="outlined">No block-local instructions.</Alert>
                )}
              </Stack>
            </SAccordion>

            {/* HB-6 (D-L2-5): Path B's live data picker is gone. One action, one
                destination — the form closes as the Generate Screens dialog
                opens, so only one modal is ever on screen (FP-4). */}
            {mode === 'edit' && blockId && onOpenScreenGeneration && (
              <Stack
                direction="row"
                spacing={2}
                alignItems="flex-start"
                justifyContent="space-between"
                sx={{ flexWrap: 'wrap' }}
              >
                <Box sx={{ flex: 1, minWidth: 240 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    Screen generation
                  </Typography>
                  {/* Designer invariant 14: a disabled control names its reason. */}
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    data-testid={STRATEGY_CANVAS.blockFormUpdateWithAIReason}
                  >
                    {screenGenerationBlocked
                      ?? 'Opens the Generate Screens dialog. Data comes from the strategy — the '
                      + 'pinned analysis, its frozen narrowing and this block\'s citations — not from this form.'}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  onClick={handleUpdateWithAI}
                  disabled={Boolean(screenGenerationBlocked) || isSubmitting}
                  title={screenGenerationBlocked}
                  aria-label="Open the Generate Screens dialog for this block"
                  data-testid={STRATEGY_CANVAS.blockFormUpdateWithAI}
                  sx={{ textTransform: 'none', flexShrink: 0 }}
                >
                  Update Block with AI
                </Button>
              </Stack>
            )}

            {/* Variable Assignment — which variables this block collects */}
            <SAccordion title="Variable Assignment" collapsible defaultExpanded={false}
              helpContent="Assign which variables this block should collect from users. The downstream screen agent will use this to create appropriate screens."
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {Object.keys(strategyVariables).length > 0 ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      Select variables this block should collect. Priority and method guide the screen agent.
                    </Typography>
                    {Object.values(strategyVariables).map((variable: any) => {
                      const isAssigned = (initialData as any)?.blockContract?.outputAssignment?.some(
                        (a: any) => a.id === variable.id
                      );
                      return (
                        <Box key={variable.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                          <Checkbox
                            size="small"
                            checked={isAssigned ?? false}
                            disabled // TODO: make editable + save to blockContract
                          />
                          <Typography variant="body2">
                            {variable.name || variable.id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ({variable.type})
                          </Typography>
                          {variable.required && (
                            <Typography variant="caption" color="error.main" sx={{ fontWeight: 600 }}>
                              required
                            </Typography>
                          )}
                        </Box>
                      );
                    })}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No variables defined on this strategy. Add variables on the strategy form first.
                  </Typography>
                )}
              </Box>
            </SAccordion>

            {/* FR-037: Screens placeholder section */}
            <SAccordion title="Screens" collapsible defaultExpanded={false}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Manage screens for this block. (Coming soon)
                </Typography>

                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  disabled
                  fullWidth
                  sx={{ justifyContent: 'flex-start' }}
                >
                  Add Screen
                </Button>
              </Box>
            </SAccordion>

            {/* DataMappingViewerV2 - shows AI data usage (edit mode only, V2 format) */}
            {mode === 'edit' && (
              <DataMappingViewerV2
                dataUsage={(initialData as any)?.dataUsage}
                defaultExpanded={true}
                title="AI Data Usage"
              />
            )}

            {/* AI Generation Section (create mode only) */}
            {mode === 'create' && (
              <SAccordion title="AI Generation (Optional)" defaultExpanded={false}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Use AI to generate screens for this block. The data comes from the strategy —
                    the server resolves it from the pinned analysis and the strategy snapshot.
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <input
                      type="checkbox"
                      id="useAI"
                      checked={formData.useAI}
                      onChange={(e) => handleFieldChange('useAI', e.target.checked)}
                      data-testid="block-form-use-ai"
                      style={{ cursor: 'pointer' }}
                    />
                    <label htmlFor="useAI" style={{ cursor: 'pointer', userSelect: 'none' }}>
                      <Typography variant="body2">
                        Generate screens with AI
                      </Typography>
                    </label>
                  </Box>

                  {formData.useAI && (
                    <>
                      <StyledTextField
                        label="AI Style (Optional)"
                        value={formData.aiStyle || ''}
                        onChange={(value) => handleFieldChange('aiStyle', value)}
                        fullWidth
                        maxLength={100}
                        placeholder="e.g., Modern, Minimalist, Professional"
                        data-testid="block-form-ai-style"
                        // FR-034: helperText removed, using placeholder instead
                      />

                      <StyledTextArea
                        label="AI Prompt (Optional)"
                        value={formData.aiContent || ''}
                        onChange={(value) => handleFieldChange('aiContent', value)}
                        placeholder="e.g., Create engaging onboarding screens with animations"
                        fullWidth
                        rows={4}
                        maxLength={500}
                        showCharacterCount
                        data-testid="block-form-ai-prompt"
                        // FR-034: helperText removed, using placeholder instead
                      />

                      <Alert severity="info">
                        <Typography variant="caption">
                          💡 AI will generate screens from the strategy&apos;s own grounding and your
                          prompt. This may take 10-30 seconds.
                        </Typography>
                      </Alert>
                    </>
                  )}
                </Box>
              </SAccordion>
            )}

            {/* FR-040: Regenerate with AI Section - REMOVED (deferred to future sprint) */}

            {/* Export JSON button — downloads the same payload that goes to builder-service */}
            {mode === 'edit' && blockId && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', pt: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportBlockJson}
                  data-testid="block-form-export-btn"
                  sx={{ textTransform: 'none', color: 'text.secondary' }}
                >
                  Export Block JSON
                </Button>
              </Box>
            )}
          </Stack>
      </SDialog>

      {/* Unsaved Changes Dialog */}
      <UnsavedChangesDialog
        open={showUnsavedDialog}
        changedFields={getChanges()}
        onSave={handleSaveAndClose}
        onDiscard={handleDiscardAndClose}
        onCancel={handleContinueEditing}
        saving={isSubmitting}
        title="Unsaved Changes Detected"
        message="You have made changes to the block form. What would you like to do with these changes?"
        saveButtonText="Save & Close"
        discardButtonText="Discard Changes"
        cancelButtonText="Continue Editing"
      />

    </>
  );
};
